import React, {useEffect, useState} from 'react'
import PlayApi from '../logic/PlayApi'
import {useNavigate, useParams} from 'react-router-dom'
import LoadingBackdrop from '../components/LoadingBackdrop'
import DeckApi from '../logic/DeckApi'
import {Card} from '../model/models'
import ImageLogic from '../logic/ImageLogic'
import Button from '@mui/material/Button'
import {Alert, Container, Divider, Fab, Grid, Icon, IconButton, Paper, Typography} from '@mui/material'
import Box from '@mui/material/Box'
import AppBar from '@mui/material/AppBar'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious'
import useMediaQuery from '@mui/material/useMediaQuery'
import {CheckCircle} from '@mui/icons-material'
import CancelIcon from '@mui/icons-material/Cancel'
import {pink} from '@mui/material/colors'
import LinearProgress, {linearProgressClasses} from '@mui/material/LinearProgress'
import {styled} from '@mui/material/styles'
import Button1 from '../components/Button1'
import {usePressObserver} from '../components/usePressObserver'
import MarkdownDisplay from '../components/MarkdownDisplay'
import CloseIcon from '@mui/icons-material/Close'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import ShortcutsDialog from '../components/ShortcutsDialog'

const playApi = new PlayApi()
const deckApi = new DeckApi()

const BorderLinearProgress = styled(LinearProgress)(({theme}) => ({
  height: 10,
  borderRadius: 3,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 3,
    backgroundColor: theme.palette.mode === 'light' ? '#1a90ff' : '#308fe8',
  },
}))

export default function Play() {
  const [initialLoaded, setInitialLoaded] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const navigate = useNavigate()
  const {deckIdParam, playTypeParam} = useParams()
  const [deckId, setDeckId] = useState<number>(-1)
  const [reversed, setReversed] = useState<boolean>(false)
  const [sessionId, setSessionId] = useState<number>(-1)
  const [cardOrder, setCardOrder] = useState<number[]>([])
  const [initialCard, setInitialCard] = useState<Card | null>(null)
  const [currentCard, setCurrentCard] = useState<Card | null>(null)
  const [totalNumCards, setTotalNumCards] = useState<number>(-1)
  const [currentCardIndex, setCurrentCardIndex] = useState<number>(-1)
  const [currentSessionCardId, setCurrentSessionCardId] = useState<number>(-1)
  const [answerVisible, setAnswerVisible] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [noCardsCondition, setNoCardsCondition] = useState<boolean>(false)
  const [finished, setFinished] = useState<boolean>(false)
  const [progressValue, setProgressValue] = useState<number>(0)
  const [finishedNumCorrect, setFinishedNumCorrect] = useState<number>(0)
  const [finishedNumIncorrect, setFinishedNumIncorrect] = useState<number>(0)
  const [finishedNumSkipped, setFinishedNumSkipped] = useState<number>(0)
  const largeScreen = useMediaQuery('(min-width:960px)')
  const spacePressed = usePressObserver({watchKey: "Space"})
  const rightArrowPressed = usePressObserver({watchKey: "ArrowRight"})
  const leftArrowPressed = usePressObserver({watchKey: "ArrowLeft"})
  const upArrowPressed = usePressObserver({watchKey: "ArrowUp"})
  const downArrowPressed = usePressObserver({watchKey: "ArrowDown"})
  const escPressed = usePressObserver({watchKey:"Escape"})

  useEffect(() => {
    if (!answerVisible) return
    window.scrollTo(0, document.body.scrollHeight)
  }, [answerVisible])

  useEffect(() => {
    if (spacePressed) {
      if (noCardsCondition || finished || loading) return

      if (answerVisible) {
        hideAnswer()
      } else {
        showAnswer()
      }
    }
  }, [spacePressed])

  useEffect(() => {
    if (rightArrowPressed) {
      nextCard()
    }
  }, [rightArrowPressed])

  useEffect(() => {
    if (escPressed) {
      navigate(`/deck/${deckId}`)
    }
  }, [escPressed])
  
 

  useEffect(() => {
    if (noCardsCondition || finished || loading) return
    if (leftArrowPressed) {
      prevCard()
    }
  }, [leftArrowPressed])

  useEffect(() => {
    if (noCardsCondition || finished || loading) return

    if (upArrowPressed) {
      processCardKnown(true)
    }
  }, [upArrowPressed])

  useEffect(() => {
    if (noCardsCondition || finished || loading) return

    if (downArrowPressed) {
      processCardKnown(false)
    }
  }, [downArrowPressed])

  useEffect(() => {
    if (finished) {
      return
    }

    if (!deckIdParam) {
      navigate('/404')
      return
    }

    const parsedDeckId = parseInt(deckIdParam)
    if (isNaN(parsedDeckId)) {
      navigate('/404')
      return
    }

    setDeckId(parsedDeckId)

    const reversed = new URLSearchParams(window.location.search).get("reversed") === 'true'
    setReversed(reversed)

    playApi.startSession(parsedDeckId)
      .then(apiResponse => {
        if (apiResponse.isError()) {
          if (apiResponse.statusCode === 404) {
            navigate('/404')
            return
          } else {
            console.error('unexpected error starting play session', apiResponse.exception?.toJson())
            navigate('/error')
            return
          }
        }

        const res = apiResponse.data!
        if (res.sessionId === -1) {
          setNoCardsCondition(true)
          setFinished(true)
          setLoading(false)
          return
        }
        setSessionId(res.sessionId)
        setTotalNumCards(res.cardOrder.length)
        setCardOrder(res.cardOrder)
        setInitialCard(res.firstCard)
        setInitialLoaded(true)
      })
  }, [])

  useEffect(() => {
    if (sessionId === -1 || deckId === -1 || initialCard === null || totalNumCards <= 0 || finished || initialLoaded) {
      return
    }

    nextCard().catch(err => {
      console.log('error loading initial card', err)
    })
  }, [sessionId, deckId, initialCard, initialLoaded])

  const nextCard = async () => {
    if (currentCardIndex >= totalNumCards - 1) {
      if (!finished) {
        await finish()
      }
      return
    }

    await loadCard(currentCardIndex + 1)
  }

  const prevCard = async () => {
    if (currentCardIndex < 0) {
      return
    }
    setFinished(false)

    await loadCard(currentCardIndex - 1)
  }

  const finish = async () => {
    setLoading(true)
    setAnswerVisible(false)
    const res = await playApi.finishSession(sessionId)
    if (res.isError() || res.data === null) {
      console.error('error finishing play session', res.exception?.toJson())
      navigate('/error')
      return
    }

    const result = res.data

    setFinishedNumCorrect(result.sessionCards.filter(sc => sc.known === true).length)
    setFinishedNumIncorrect(result.sessionCards.filter(sc => sc.known === false).length)
    setFinishedNumSkipped(result.sessionCards.filter(sc => sc.known === null).length)
    
    console.log('num sessionCards: ' + result?.sessionCards.length)
    console.log('skipped: ')
    console.log(result.sessionCards.filter(sc => sc.known === null))
    console.log(result.sessionCards)
    

    setFinished(true)
    setCurrentCard(null)
    setCurrentCardIndex(totalNumCards)
    setLoading(false)
    return
  }

  const loadCard = async (orderIndex: number) => {
    setLoading(true)

    if (orderIndex < 0 || orderIndex >= totalNumCards) {
      console.error('cannot load card with order index ' + orderIndex)
      setLoading(false)
      return
    }

    const cardId = cardOrder[orderIndex]
    const cardResponse = await deckApi.getCard(deckId, cardId)
    if (cardResponse.isError()) {
      console.log('error getting card', cardResponse.exception?.toJson())
      navigate('/error')
      return
    }
    const card = cardResponse.data!

    let sessionCardIdResponse = await playApi.startCard(sessionId, deckId, card.id)
    if (sessionCardIdResponse.isError()) {
      console.error('error with startCard request', sessionCardIdResponse.exception?.toJson())
      navigate(('/error'))
      return
    }

    const sessionCardId = sessionCardIdResponse.data!.id
    setCurrentSessionCardId(sessionCardId)

    if (reversed) {
      let tempFrontText = card.frontText
      let tempFrontImageMeta = card.frontImageMeta
      card.frontText = card.backText
      card.frontImageMeta = card.backImageMeta
      card.backText = tempFrontText
      card.backImageMeta = tempFrontImageMeta
    }

    setCurrentCard(card)
    setCurrentCardIndex(orderIndex)
    setAnswerVisible(false)

    const newProgressValue = ((orderIndex + 1) / totalNumCards) * 100
    setProgressValue(newProgressValue)

    setLoading(false)
  }

  const processCardKnownFromClick = async (ev: any, cardKnown: boolean) => {
    ev.preventDefault()
    await processCardKnown(cardKnown)
  }

  const processCardKnown = async (cardKnown: boolean) => {
    setLoading(true)

    const res = await playApi.updateCard(sessionId, currentSessionCardId, deckId, currentCard!.id, cardKnown)
    if (res.isError()) {
      console.error('error updating session card', res.exception?.toJson())
      navigate('/error')
      return
    }

    await nextCard()
  }

  const showAnswer = () => {
    setAnswerVisible(true)
  }

  const hideAnswer = () => {
    setAnswerVisible(false)
  }

  return (
    <>
      <Container id="MainContainer" maxWidth={false} sx={{margin: 0, padding: '0 !important'}}>
        <Box sx={{margin: 0, padding: 0}}>
          <Container maxWidth="lg">
            <LoadingBackdrop loading={loading}/>
            {error && <Alert severity="error">{error}</Alert>}
            {currentCard &&

              <Grid sx={{mt: 0, mb: {xs: '115px', sm: 0}, minHeight: {sm: '360px'}}} container spacing={1}>

                <Grid item xs={12} md={6} sx={{}}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 1, mt: 0, flexGrow: 1
                    }}>
                    {currentCard.frontImageMeta &&
                      <Box sx={{textAlign: 'center'}}>

                        <Box
                          component="img"
                          sx={{maxWidth: 1, maxHeight: '360px', pb: .5}}
                          alt={currentCard.frontImageMeta && currentCard.frontImageMeta.alt}
                          src={currentCard.frontImageMeta && ImageLogic.getImageUrl(currentCard.frontImageMeta)}
                        /></Box>}
                    {currentCard.frontText &&
                      <Box sx={{mx: 1.5}}><MarkdownDisplay value={currentCard.frontText}/></Box>}
                  </Paper>
                </Grid>

                {answerVisible && !loading &&
                  <>
                    <Grid item xs={12} md={6} sx={{}}>
                      <Paper
                        elevation={3}
                        sx={{
                          p: 1, mt: 0, flexGrow: 1
                        }}>
                        {currentCard.backImageMeta &&
                          <Box sx={{textAlign: 'center'}}><Box
                            component="img"
                            sx={{maxWidth: 1, maxHeight: '360px', pb: .5}}
                            alt={currentCard.backImageMeta && currentCard.backImageMeta.alt}
                            src={currentCard.backImageMeta && ImageLogic.getImageUrl(currentCard.backImageMeta)}
                          />
                          </Box>}
                        {currentCard.backText &&
                          <Box sx={{mx: 1.5}}> <MarkdownDisplay value={currentCard.backText}/></Box>}
                      </Paper>
                    </Grid>
                  </>
                }
              </Grid>
            }
            {!largeScreen && !finished &&

              <AppBar position="fixed" color="primary" sx={{top: 'auto', bottom: 0}}>
                <Grid container sx={{justifyContent: 'space-between'}}>

                  {totalNumCards > 0 && !finished &&
                    <Grid item xs={12} sx={{display: 'flex', pb: 0, pt: .5, m: 0}}>
                      <IconButton sx={{}} onClick={prevCard} disabled={currentCardIndex <= 0}>
                        <SkipPreviousIcon/>
                      </IconButton>
                      {!answerVisible &&
                        <Button
                          variant="outlined"
                          sx={{width: '100%', py: 1}}
                          onClick={showAnswer}
                          disabled={loading || totalNumCards <= 0 || finished}
                        >Show Answer</Button>
                      }
                      {answerVisible &&
                        <Button
                          variant="outlined"
                          sx={{width: '100%', py: 1}}
                          onClick={hideAnswer}>Hide Answer</Button>
                      }
                      <IconButton sx={{}} onClick={nextCard} disabled={loading || finished}>
                        <SkipNextIcon/>
                      </IconButton>
                    </Grid>
                  }

                  <Grid item xs={12} sx={{p: 1, m: 0}}>
                    <Grid container>

                      <Grid item xs={6} sx={{pr: 0.5}}>
                        <Button
                          sx={{width: '100%', py: 1}}
                          variant="outlined"
                          onClick={e => processCardKnownFromClick(e, true)}
                          disabled={loading || totalNumCards <= 0}
                          startIcon={<CheckCircle color='success'/>}>
                          Got It
                        </Button>
                      </Grid>
                      <Grid item xs={6} sx={{pl: 0.5}}>
                        <Button
                          variant="outlined"
                          sx={{width: '100%', py: 1}}
                          onClick={e => processCardKnownFromClick(e, false)}
                          startIcon={<CloseIcon sx={{color: pink[600]}}/>}>
                          Not Yet
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item>
                    <Box sx={{width: '100%'}}>
                      <BorderLinearProgress variant="determinate" value={progressValue}/>
                    </Box>
                  </Grid>

                </Grid>

                <Box sx={{mt: 0}}>
                  <BorderLinearProgress variant="determinate" value={progressValue}/>
                </Box>
              </AppBar>
            }

            {!largeScreen && finished &&
              <AppBar position="fixed" color="primary" sx={{top: 'auto', bottom: 0}}>
                <Grid container>
                  <Grid item xs={12} sx={{m: 0}}>
                    <Button1
                      onClick={_ => navigate(`/deck/${deckId}`)}>
                      Back to Deck
                    </Button1>
                  </Grid>
                </Grid>
              </AppBar>
            }

            {largeScreen && !finished &&
              <Grid container>
                <Grid sm={2} md={3} item>

                </Grid>
                <Grid item sm={8} md={6}>
                  <Box>
                    <Grid container sx={{pt: 1}}>
                      <Grid item sx={{pt: 1}}>
                        <IconButton onClick={prevCard}
                                    disabled={loading || currentCardIndex === 0 || totalNumCards <= 0}>
                          <SkipPreviousIcon/>
                        </IconButton>
                      </Grid>
                      <Grid item sx={{pt: 1}}>
                        {!answerVisible &&
                          <Button
                            variant="outlined"
                            sx={{
                              fontSize: '16px',
                              textTransform: 'none',
                              display: "flex",
                              alignItems: "center",
                              width: '145px',
                              color: "white"
                            }} onClick={showAnswer}
                            disabled={loading || finished || totalNumCards <= 0}
                          >Show Answer</Button>
                        }
                        {answerVisible &&
                          <Button
                            variant="outlined"
                            sx={{
                              fontSize: '16px',
                              textTransform: 'none',
                              display: "flex",
                              alignItems: "center",
                              width: '145px',
                              color: "white"
                            }}
                            onClick={hideAnswer}
                          >Hide Answer</Button>
                        }
                      </Grid>
                      <Grid item sx={{pt: 1}}>
                        <IconButton disabled={loading || finished || totalNumCards <= 0} onClick={nextCard}>
                          <SkipNextIcon/>
                        </IconButton>
                      </Grid>
                      {!loading && !finished && totalNumCards > 0 &&
                        <Grid item sx={{display: "flex", ml: 2}} alignItems="center">
                          <Divider orientation="vertical" flexItem/>
                          <Typography sx={{ml: 1}} variant={'body1'}>Got it right?</Typography>
                          <IconButton sx={{ml: 1}} onClick={e => processCardKnownFromClick(e, true)}>
                            <CheckCircle fontSize='large' color='success'/></IconButton>
                          <IconButton onClick={e => processCardKnownFromClick(e, false)}>
                            <CancelIcon fontSize="large" sx={{color: pink[600]}}/></IconButton>
                          <Divider orientation="vertical" flexItem/>

                        </Grid>
                      }
                      <Grid item>
                        <Typography sx={{m: 2}}>Card: {currentCardIndex + 1}/{totalNumCards}</Typography>
                      </Grid>

                    </Grid>
                    <Box sx={{mt: 2}}>
                      <BorderLinearProgress variant="determinate" value={progressValue}/>

                    </Box>
                    <Grid item xs={12}
                          sx={{mt: 2, display: "flex", alignItems: "top", justifyContent: 'space-between'}}>
                      <Box sx={{display: "flex", textAlign: 'center', alignItems: "center"}}>
                        <Typography>Keyboard Shortcuts</Typography>
                        <ShortcutsDialog/>
                      </Box>
                      <Fab
                        sx={{
                          fontSize: '16px',
                          textTransform: 'none',
                          alignItems: "center",
                          left: 'auto',
                          fontWeight: 900,
                        }}
                        color="secondary"
                        variant="extended" size="medium"
                        onClick={_ => navigate(`/deck/${deckId}`)}>
                        Exit
                      </Fab>
                    </Grid>
                  </Box>

                </Grid>
                <Grid item sm={2} md={3}>

                </Grid>
              </Grid>

            }
            {!largeScreen && !finished &&
              <Fab
                sx={{
                  right: 5,
                  bottom: 117,
                  left: 'auto',
                  fontWeight: 900,
                  position: 'fixed'
                }}
                color="secondary"
                size="small"
                onClick={_ => navigate(`/deck/${deckId}`)}>
                <ExitToAppIcon/>
              </Fab>
            }

            {finished && <Grid>
              {noCardsCondition && <p>No cards</p>}
              {!noCardsCondition &&
                <Paper
                  sx={{
                    p: 2, mt: 1, flexGrow: 1
                  }}>
                  <Grid container>
                    <Grid item xs={0} sm={3}> </Grid>
                    <Grid item xs={12} sm={6} sx={{justifyContent: 'center'}}>
                      <Box display='flex' sx={{justifyContent: 'center'}}>

                        <CheckCircle fontSize='large' color='success'/>
                        <Typography sx={{ml: 1}} variant={'h6'}
                                    gutterBottom={true}> Correct: {finishedNumCorrect} / {totalNumCards} </Typography>
                      </Box>
                      <Box display='flex' sx={{justifyContent: 'center'}}>
                        <CancelIcon fontSize="large" sx={{color: pink[600]}}/>
                        <Typography variant={'h6'} gutterBottom={true}
                                    sx={{ml: 1}}>Incorrect: {finishedNumIncorrect} / {totalNumCards} </Typography>
                      </Box>
                      <Box display='flex' sx={{justifyContent: 'center'}}>
                        <Typography
                          sx={{color: 'info.main', mt: 1}}
                          variant={'h2'}
                          gutterBottom={true}>
                          {Math.round((finishedNumCorrect / totalNumCards) * 100)} %
                        </Typography>
                      </Box>
                      <Box>Skipped: {finishedNumSkipped}</Box>

                      <Box display='flex' sx={{justifyContent: 'center'}}>
                        <Typography sx={{mt: 2}} variant={'h2'} gutterBottom={true}>You're Learning!</Typography>
                      </Box>
                      <Box display='flex' sx={{justifyContent: 'center'}}>
                        <Typography
                          align='center'
                          sx={{mt: 2}}
                          variant={'h6'}
                          gutterBottom={true}>
                          Keep up the great work! The best way to improve is to continue to practice and test yourself.
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={0} sm={3}> </Grid>
                    {/*<Box sx={{ml: 2}}>*/}
                    {/*  <Typography variant={'h6'} gutterBottom={true}>Cards Correct: {finishedNumCorrect}</Typography>*/}
                    {/*  <Typography variant={'h6'} gutterBottom={true}>Cards Incorrect: {finishedNumIncorrect}</Typography>*/}
                    {/*  <Typography variant={'h6'} gutterBottom={true}>Cards Skipped: {finishedNumSkipped}</Typography>*/}
                    {/*</Box>*/}
                  </Grid>
                </Paper>}

              {largeScreen &&
                <Box sx={{width: '170px', mt: 2}}><Button1 onClick={_ => navigate(`/deck/${deckId}`)}>Back to
                  Deck</Button1></Box>}
            </Grid>}
          </Container>
        </Box>
      </Container>
    </>
  )
}
